/**
 * The fleet: who drives, what they drive, whose paperwork is about to lapse, and who is in
 * the office.
 *
 * The page that turns onboarding into a form all the way through. Everything else in Phase 8
 * — provisioning, branding, feature switches, importing customers — was a screen; putting the
 * first driver in a car was rows written into the firm's database by hand, which is a step a
 * runbook cannot honestly ask a customer to perform.
 *
 * Wider than its name and narrower than three sections: people, cars and the office are one
 * page because they are one job. A firm hires a driver, gives him a car and a set of
 * documents, and puts somebody on the telephone; three sidebar entries would make an
 * administrator cross the console to finish one afternoon's work.
 *
 * What is expiring comes first, above everything else, and that is deliberate. It is the only
 * part of this page with a deadline on it: everything else is here when somebody comes
 * looking, while a lapsing insurance certificate is a driver who cannot sign on next Tuesday.
 * The index behind it was built in Phase 3 with a comment saying it was for exactly this and
 * nothing had ever read it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { devicesApi } from '../../devices/api';
import { refusal } from '../errors';
import {
  Banner,
  FieldErrors,
  PageHeader,
  type FieldProblem,
} from '../../ui';
import { DriverList } from './DriverList';
import { WhoDrivesWhat } from './WhoDrivesWhat';
import { ExpiringList } from './ExpiringList';
import { StaffPanel } from './StaffPanel';
import { VehicleList } from './VehicleList';
import { useFadingNote } from './fading';
import {
  fleetApi,
  type ExpiringDocument,
  type FleetCategory,
  type FleetDriver,
  type FleetFeature,
  type FleetVehicle,
  type HireRequest,
  type NewDocument,
  type NewDriver,
  type NewVehicle,
} from './api';

export function FleetPage() {
  const { t } = useTranslation();

  const [drivers, setDrivers] = useState<FleetDriver[] | null>(null);
  const [vehicles, setVehicles] = useState<FleetVehicle[] | null>(null);
  const [categories, setCategories] = useState<FleetCategory[]>([]);
  const [features, setFeatures] = useState<FleetFeature[]>([]);
  const [anyCar, setAnyCar] = useState<boolean | null>(null);
  const [expiring, setExpiring] = useState<ExpiringDocument[]>([]);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [note, setNote] = useFadingNote();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDrivers(await fleetApi.drivers());
      setVehicles(await fleetApi.vehicles());
      setCategories(await fleetApi.categories());
      setFeatures(await fleetApi.features());
      setAnyCar((await fleetApi.settings()).driversMayTakeAnyCar);
      setExpiring(await fleetApi.expiring());
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  /**
   * Every mutation on this page, wrapped the same way.
   *
   * A refetch after each one rather than patching state by hand. These records interact —
   * retiring a car signs its driver out of it, offboarding a driver frees the car, and a new
   * document changes what is on the expiry list — so a page that updated only the row that
   * was touched would be wrong in three different places and right in one.
   *
   * It answers whether the mutation went through, which is what a dialog needs in order to
   * close only once there is something to close over.
   */
  async function run(action: () => Promise<string | null>): Promise<boolean> {
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      const said = await action();

      if (said !== null) setNote(said);

      await load();

      return true;
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotSave'));

      return false;
    } finally {
      setBusy(false);
    }
  }

  function hire(request: HireRequest) {
    return run(async () => {
      const hired = await fleetApi.hire(request);

      // Said rather than assumed. Whether an invitation was sent depends on whether the
      // address already had a password, which the office cannot know and would otherwise
      // wait on — or worse, tell the driver to expect an email that was never sent.
      return hired.inviteExpiresAt === null
        ? t('fleet.drivers.existingAccount', { email: request.email })
        : t('fleet.drivers.invited', { email: request.email });
    });
  }

  /** A driver who signs in on a service device, put on the books with no address. */
  async function hireForDevice(driver: NewDriver): Promise<string | null> {
    let created: string | null = null;

    const done = await run(async () => {
      const hired = await devicesApi.createDriver({ driver, vehicleId: null, newVehicle: null });
      created = hired.id;

      return t('devices.hire.done', { name: hired.displayName });
    });

    return done ? created : null;
  }

  function setDriverActive(driver: FleetDriver, active: boolean) {
    void run(async () => {
      const result = await fleetApi.setDriverActive(driver.id, active);

      if (active) return t('fleet.drivers.reinstated', { name: driver.displayName });

      // The two halves of offboarding are not always both possible, and the difference
      // matters: somebody who also takes calls keeps their console access on purpose.
      return result.membershipChanged
        ? t('fleet.drivers.offboarded', { name: driver.displayName })
        : t('fleet.drivers.offboardedKeepsRole', { name: driver.displayName });
    });
  }

  function recordDocument(document: NewDocument) {
    return run(async () => {
      await fleetApi.recordDocument(document);
      return null;
    });
  }

  function removeDocument(documentId: string) {
    void run(async () => {
      await fleetApi.removeDocument(documentId);
      return null;
    });
  }

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <>
        <PageHeader
          title={t('nav.settings.fleet')}
          description={t('nav.settingsAbout.fleet')}
        />

        {note !== null && <Banner tone="ok">{note}</Banner>}
        {error !== null && <Banner tone="danger">{error.message}</Banner>}

        <ExpiringList documents={expiring} />

        <WhoDrivesWhat
          anyCar={anyCar}
          busy={busy}
          onChange={(next) =>
            void run(async () => {
              setAnyCar((await fleetApi.saveSettings({ driversMayTakeAnyCar: next })).driversMayTakeAnyCar);
              return null;
            })
          }
        />

        <DriverList
          drivers={drivers}
          vehicles={vehicles}
          anyCar={anyCar ?? true}
          busy={busy}
          error={error?.message ?? null}
          onHire={hire}
          onHireForDevice={hireForDevice}
          onUpdate={(driverId: string, driver: NewDriver) =>
            run(async () => {
              await fleetApi.updateDriver(driverId, driver);
              return null;
            })
          }
          onSetActive={setDriverActive}
          onRecordDocument={recordDocument}
          onRemoveDocument={removeDocument}
          onSetAllowed={(driverId, vehicleIds) =>
            run(async () => {
              await fleetApi.setAllowedVehicles(driverId, vehicleIds);
              return null;
            })
          }
        />

        <StaffPanel />

        <VehicleList
          vehicles={vehicles}
          categories={categories}
          features={features}
          busy={busy}
          error={error?.message ?? null}
          onRegister={(vehicle: NewVehicle) =>
            run(async () => {
              await fleetApi.registerVehicle(vehicle);
              return null;
            })
          }
          onUpdate={(vehicleId: string, vehicle: NewVehicle) =>
            run(async () => {
              await fleetApi.updateVehicle(vehicleId, vehicle);
              return null;
            })
          }
          onSetActive={(vehicle: FleetVehicle, active: boolean) =>
            void run(async () => {
              await fleetApi.setVehicleActive(vehicle.id, active);
              return null;
            })
          }
          onRecordDocument={recordDocument}
          onRemoveDocument={removeDocument}
        />
      </>
    </FieldErrors>
  );
}
